import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead, { SITE_URL, SITE_NAME } from '../components/SEOHead';
import './OurWorkDetail.css';

// react-snap prerenders with userAgent "ReactSnap", using an old bundled
// Chromium that can crash parsing modern syntax in third-party embed scripts
// (confirmed with Google Maps' embed script — see ContactMap.js). YouTube's
// embed player is the same class of risk and untested against it, so skip it
// during prerender too; real visitors still get the live embed on hydration.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/media/'}${path}`;
};

const OurWorkDetail = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [displayPhotoSrc, setDisplayPhotoSrc] = useState('');
  const photoTouchStartYRef = useRef(null);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await api.get(`/content/our-work/${slug}/`);
        setItem(res.data);
      } catch (e) {
        setError(i18n.language === 'ka' ? 'ნამუშევრის ჩატვირთვა ვერ მოხერხდა' : 'Failed to load work item');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [slug, i18n.language]);

  const localized = useMemo(() => {
    if (!item) return null;
    const isKa = i18n.language === 'ka';
    const title = (isKa ? (item.title_ka || item.title_en) : (item.title_en || item.title_ka)) || '';
    const longDesc = (isKa ? (item.long_description_ka || item.long_description_en) : (item.long_description_en || item.long_description_ka)) || '';
    const shortDesc = (isKa ? (item.short_description_ka || item.short_description_en) : (item.short_description_en || item.short_description_ka)) || '';
    const categoryTitle = (isKa ? (item.category?.title_ka || item.category?.title_en) : (item.category?.title_en || item.category?.title_ka)) || '';
    const seoTitle = (isKa ? (item.meta_title_ka || item.meta_title_en) : (item.meta_title_en || item.meta_title_ka)) || '';
    const seoDescription = (isKa ? (item.meta_description_ka || item.meta_description_en) : (item.meta_description_en || item.meta_description_ka)) || '';

    const galleryPhotos = [];
    const mainPhotoUrl = getImageUrl(item.main_photo);
    const mainPhotoRaw = (item.main_photo || '').toString().trim();
    const mainPhotoName = mainPhotoRaw.split('/').pop();
    let fallbackHeroUrl = null;

    (item.secondary_photos || []).forEach((p) => {
      const url = getImageUrl(p.photo);
      if (!url) return;
      const raw = (p.photo || '').toString().trim();
      const name = raw.split('/').pop();
      if (!fallbackHeroUrl) fallbackHeroUrl = url;
      if ((mainPhotoUrl && url === mainPhotoUrl) || (mainPhotoRaw && raw === mainPhotoRaw) || (mainPhotoName && name === mainPhotoName)) {
        return;
      }
      galleryPhotos.push({ url });
    });

    const heroUrl = mainPhotoUrl || fallbackHeroUrl;
    return { title, longDesc, shortDesc, categoryTitle, seoTitle, seoDescription, heroUrl, galleryPhotos };
  }, [item, i18n.language]);

  const descriptionLabel = i18n.language === 'ka' ? 'აღწერა' : 'Description';
  const photosLabel = i18n.language === 'ka' ? 'ფოტოები' : 'Photos';

  const galleryPhotos = useMemo(() => localized?.galleryPhotos ?? [], [localized]);
  const hasDescription = Boolean(localized?.longDesc && String(localized.longDesc).trim());
  const hasPhotos = galleryPhotos.length > 0;
  const nextPhoto = () => {
    if (galleryPhotos.length <= 1) return;
    setActivePhotoIndex((prev) => (prev + 1) % galleryPhotos.length);
  };
  const prevPhoto = () => {
    if (galleryPhotos.length <= 1) return;
    setActivePhotoIndex((prev) => (prev - 1 + galleryPhotos.length) % galleryPhotos.length);
  };

  useEffect(() => {
    if (activePhotoIndex >= galleryPhotos.length) {
      setActivePhotoIndex(0);
    }
  }, [activePhotoIndex, galleryPhotos.length]);

  useEffect(() => {
    if (!galleryPhotos.length) {
      setDisplayPhotoSrc('');
      return;
    }
    // Initialize visible photo and preload all for smooth switching.
    setDisplayPhotoSrc(galleryPhotos[0].url);
    galleryPhotos.forEach((photo) => {
      const img = new window.Image();
      img.src = photo.url;
    });
  }, [galleryPhotos]);

  useEffect(() => {
    const targetSrc = galleryPhotos[activePhotoIndex]?.url;
    if (!targetSrc) return;
    if (targetSrc === displayPhotoSrc) return;

    let cancelled = false;
    const nextImage = new window.Image();
    nextImage.onload = () => {
      if (!cancelled) setDisplayPhotoSrc(targetSrc);
    };
    nextImage.src = targetSrc;

    return () => {
      cancelled = true;
    };
  }, [activePhotoIndex, galleryPhotos, displayPhotoSrc]);

  useEffect(() => {
    if (galleryPhotos.length <= 1) return undefined;
    const timer = window.setTimeout(() => {
      setActivePhotoIndex((prev) => (prev + 1) % galleryPhotos.length);
    }, 5500);
    return () => window.clearTimeout(timer);
  }, [galleryPhotos.length, activePhotoIndex]);

  if (loading) {
    return <div className="our-work-detail-loading">{i18n.language === 'ka' ? 'იტვირთება...' : 'Loading...'}</div>;
  }

  if (error || !localized) {
    return (
      <div className="our-work-detail-error">
        <div>{error || (i18n.language === 'ka' ? 'ვერ მოიძებნა' : 'Not found')}</div>
      </div>
    );
  }

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: localized.title,
      description: (localized.shortDesc || localized.longDesc).slice(0, 155),
      image: item.main_photo || undefined,
      creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      url: `${SITE_URL}/history/our-work/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Our Work', item: `${SITE_URL}/history/our-work` },
        { '@type': 'ListItem', position: 2, name: localized.title, item: `${SITE_URL}/history/our-work/${slug}` },
      ],
    },
  ];

  return (
    <div className="our-work-detail-page">
      <SEOHead
        title={localized.seoTitle || `${localized.title} | GFM Studio`}
        description={localized.seoDescription || (localized.shortDesc || localized.longDesc).slice(0, 155)}
        path={`/history/our-work/${slug}`}
        image={item.og_image || item.main_photo || undefined}
        noindex={item.robots_index === false}
        jsonLd={jsonLd}
      />
      <div className="our-work-detail-layout">
        <section className="our-work-detail-hero">
          {localized.heroUrl ? (
            <div className="our-work-detail-hero-media">
              <img src={localized.heroUrl} alt={localized.title} className="our-work-detail-hero-image" />
              <div className="our-work-detail-hero-overlay" />
              <div className="our-work-detail-hero-caption">
                <h1 className="our-work-detail-title">{localized.title}</h1>
              </div>
            </div>
          ) : null}
        </section>

        <div className={`our-work-detail-info-grid ${hasDescription && hasPhotos ? 'two-col' : 'one-col'}`}>
          <section className="our-work-detail-section">
            <h2 className="our-work-detail-section-title">{descriptionLabel}</h2>
            {hasDescription ? (
              <div className="our-work-detail-long">
                {String(localized.longDesc)
                  .split('\n')
                  .map((p, idx) => (p.trim() ? <p key={idx}>{p}</p> : null))}
              </div>
            ) : (
              <p className="our-work-detail-empty">
                {i18n.language === 'ka' ? 'აღწერა მალე დაემატება' : 'Description coming soon'}
              </p>
            )}
          </section>

          {hasPhotos ? (
            <section className="our-work-detail-section our-work-detail-section-photos">
              <h2 className="our-work-detail-section-title">{photosLabel}</h2>
              <div className="our-work-detail-gallery">
                <div className="our-work-detail-gallery-item is-active">
                  <img
                    key={`gallery-active-${displayPhotoSrc || 'empty'}`}
                    className="our-work-detail-gallery-image-vertical-change"
                    src={displayPhotoSrc || galleryPhotos[0]?.url}
                    alt={`${localized.title} ${Math.max(1, activePhotoIndex + 1)}`}
                    loading="lazy"
                    onTouchStart={(e) => {
                      photoTouchStartYRef.current = e.changedTouches?.[0]?.clientY ?? null;
                    }}
                    onTouchEnd={(e) => {
                      const startY = photoTouchStartYRef.current;
                      const endY = e.changedTouches?.[0]?.clientY ?? null;
                      photoTouchStartYRef.current = null;
                      if (startY == null || endY == null) return;
                      const deltaY = startY - endY;
                      if (Math.abs(deltaY) < 35) return;
                      if (deltaY > 0) nextPhoto();
                      else prevPhoto();
                    }}
                  />
                </div>
                {galleryPhotos.length > 1 ? (
                  <div className="our-work-detail-gallery-dots">
                    {galleryPhotos.map((_, idx) => (
                      <button
                        key={`gallery-dot-${idx}`}
                        type="button"
                        className={`our-work-detail-gallery-dot ${idx === activePhotoIndex ? 'active' : ''}`}
                        onClick={() => setActivePhotoIndex(idx)}
                        aria-label={`Go to photo ${idx + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>

        {item.youtube_embed_url && !isPrerender ? (
          <section className="our-work-detail-section-video">
            <div className="our-work-detail-video-frame">
              <iframe
                src={item.youtube_embed_url}
                title={`${localized.title} video`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </section>
        ) : null}

        {!localized.heroUrl && !item.youtube_embed_url && !hasDescription && !hasPhotos ? (
          <p className="our-work-detail-empty">
            {i18n.language === 'ka' ? 'კონტენტი მალე დაემატება' : 'Content coming soon'}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default OurWorkDetail;
